import {useCallback, useEffect, useState} from 'react';
import {DropdownChoices} from './functions/dropdown-choice';
import {useRouter} from 'next/router';
import {dvcOhmOptions, getSpeakerCountOptions, svcOhmOptions, voiceCoilOptions} from './paths';
import {ImageViewer} from './functions/image-viewer';
import {NoPrint} from './widgets/no-print';

export function WiringDiagrams(props) {
  const getFormDefaults = useCallback(
    () => ({
      voiceCoil: props?.choices?.voiceCoil ?? '0',
      speakersCount: props?.choices?.speakersCount ?? '0',
      ohmOption: props?.choices?.ohmOption ?? '0'
    }),
    [props.choices]
  );

  //	const [loading, setLoading] = useState(false);
  const [form, setFormState] = useState(getFormDefaults());
  const [results, setResults] = useState(null);
  const router = useRouter();

  // when props.choices changes
  useEffect(() => {
    setFormState(getFormDefaults());
  }, [props.choices, getFormDefaults]);

  const step = (newState = null) => {
    const localForm = newState ?? form;
    return (
      0 +
      (localForm?.voiceCoil > '0' ? 1 : 0) +
      (localForm?.speakersCount > '0' ? 1 : 0) +
      (localForm.ohmOption > '0' ? 1 : 0)
    );
  };
  // console.log({ step: step() });

  // change state and update URL
  const setForm = async (newState) => {
    setFormState(newState);

    const urlParams = new URLSearchParams();
    Object.entries(newState).forEach(([k, v]) => urlParams.append(k, v));
    await router.replace('?' + urlParams);

    const stepTitles = [
      'Wiring Diagrams: Select Subwoofer QuantitySelector',
      'Wiring Diagrams: Select Impedance',
      'Wiring Diagrams: ' + (results ? Object.values(results).join(', ') : null)
    ];
    const stepTitle = stepTitles?.[step()];
    document.title = stepTitle;

    if (step(newState) >= 2) {
      onFinalStep(newState);
    }
  };

  const resetForm = () => {
    setFormState(getFormDefaults());
    // Clear URL params
    router.replace(router.pathname);
  };

  const handleFormChange = (e) => {
    const {name, value} = e.target;
    setForm({...form, [name]: value});
  };

  const onBack = () => {
    setResults(null);
    resetForm();
    props.resetChoices();
  };

  const onFinalStep = (newState) => {
    // console.log({ speakersCount: newState.speakersCount })
    setResultLabels(newState);
    props.didChoose({...newState});
  };

  const getSpeakerTypeName = () => {
    return form?.voiceCoil === '1' ? 'Speaker' : 'Subwoofer';
  };

  const setResultLabels = (newState) => {
    const speakersCountOptions = getSpeakerCountOptions(getSpeakerTypeName());
    const ohmOptions = newState.voiceCoil === '1' ? svcOhmOptions : dvcOhmOptions;
    const res = {
      voiceCoil: voiceCoilOptions.find((e) => e.value === newState.voiceCoil).label,
      ohmOption: ohmOptions.find((e) => e.value === newState.ohmOption)?.label,
      speakersCount: speakersCountOptions.find((e) => e.value === newState.speakersCount)?.label
    };
    setResults(res);
  };

  return (
    <div className="px-0">
      <form className="mx-auto px-0 py-0">
        <div
          className="header-container mx-auto flex-row mt-1 mb-1"
          style={{display: 'flex', alignItems: 'center', justifyContent: 'space-between'}}>
          <h3 className="text-3xl font-oswald uppercase mt-1">Wiring Diagrams</h3>

          <button
            type="button"
            className="flex gap-2 items-center py-2 px-4 text-sm font-medium text-white rounded-md border bg-neutral-700 hover:bg-neutral-800"
            onClick={resetForm}>
            Reset
          </button>
        </div>

        <NoPrint>
          <DropdownChoices
            show={true}
            header={
              <>
                <strong>STEP 1</strong>: Subwoofer Quantity
              </>
            }
            qs="How many subwoofers"
            options={getSpeakerCountOptions(getSpeakerTypeName())}
            name="speakersCount"
            value={form.speakersCount}
            onChange={(v) => {
              setForm({...form, speakersCount: v});
            }}
            checked={step() >= 1}
          />

          <DropdownChoices
            show={step() >= 1}
            header={
              <>
                <strong>STEP 2</strong>: Impedance
              </>
            }
            qs="What is your subwoofer impedance"
            options={dvcOhmOptions}
            name="ohmOption"
            value={form.ohmOption}
            onChange={(v) => {
              setForm({...form, ohmOption: v});
            }}
            checked={step() >= 2}
          />
        </NoPrint>

        {step() >= 2 && (
          <>
            <ImageViewer choices={form} />
          </>
        )}
      </form>
    </div>
  );
}
